// Forms (R-F1…R-F8, July blueprint 14b): a form belongs to one scope record, has steps and fields; a response is saved first, then a scenario processes it.

import type { Id } from './model.ts';

export type FormFieldKind =
  | 'text' | 'email' | 'phone' | 'textarea' | 'number' | 'date' | 'url'
  | 'select' | 'multiselect' | 'checkbox' | 'consent' | 'heading'
  /** Quantities of records of a type (offers of the event): «Стандарт × 2». */
  | 'record_quantities'
  /** A list of people (name + email): co-speakers, participants. */
  | 'people'
  /** An attached file (R-F6): allowed extensions, a size limit; kept in the file storage, the answer holds a reference. */
  | 'file';

/** The answer of a file field: where the file lies and what the person sent. */
/** A stored file of an answer; `sha256` is the checksum taken at upload, checked again at every download (architecture rule 13). */
export interface FileRef { key: string; name: string; size: number; type: string; sha256?: string }
export const isFileRef = (v: unknown): v is FileRef => !!v && typeof v === 'object' && typeof (v as FileRef).key === 'string' && typeof (v as FileRef).name === 'string';
/** Defaults of a file field: slides and photos fit, a video does not (decision 014: «пока достаточно» a plain attachment). */
export const FILE_DEFAULTS = { maxMb: 20, accept: ['pdf', 'ppt', 'pptx', 'key', 'odp', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'webp', 'zip'] };
/** Checks a file against the field: the extension from the list, the size within the limit. */
export function fileProblem(f: Pick<FormField, 'accept' | 'maxMb'>, name: string, size: number): string | null {
  const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : '';
  const accept = f.accept?.length ? f.accept : FILE_DEFAULTS.accept;
  if (!accept.includes(ext)) return `Подходят файлы: ${accept.join(', ')}`;
  const max = f.maxMb ?? FILE_DEFAULTS.maxMb;
  if (size > max * 1024 * 1024) return `Файл больше ${max} МБ`;
  if (!size) return 'Файл пустой';
  return null;
}

export interface FormField {
  id: string;
  /** Machine key of the answer; processors read answers by key (name, email, title…). */
  key: string;
  label: string;
  kind: FormFieldKind;
  required?: boolean;
  help?: string;
  placeholder?: string;
  options?: string[];
  /** Shown only when another field has this value (simple conditional rule). */
  showIf?: { field: string; equals: string | boolean | string[] };
  /** record_quantities: records of this type linked to the form's scope record, optionally only those whose field value is in the list. */
  typeId?: string;
  recordFilter?: { field: string; in: string[] };
  priceField?: string;
  descField?: string;
  /** Shown as «Осталось мест» when quota − sold is small. */
  quotaField?: string;
  soldField?: string;
  /** The quota of a record whose field holds one of these values only informs: the counter shows, «Места закончились» and the lock do not. */
  quotaSoft?: { field: string; in: string[] };
  /** people: how many at most; number: bounds. */
  max?: number;
  min?: number;
  /** A text answer must match this regular expression (ИНН — 10 или 12 цифр); `patternText` explains it. */
  pattern?: string;
  patternText?: string;
  /** file: allowed extensions without the dot, and the size limit in megabytes. */
  accept?: string[];
  maxMb?: number;
  /** A field the form type needs; it can be edited but not removed. */
  locked?: boolean;
}

export interface FormStep { id: string; title: string; description?: string; fields: FormField[] }

export interface FormSettings {
  description?: string;
  opensAt?: string | null;
  closesAt?: string | null;
  submitLabel?: string;
  successTitle?: string;
  successText?: string;
}

export type FormStatus = 'draft' | 'published' | 'closed';

/** Form types apps register: what the form is for and which command processes a response. */
export interface FormTypeDef {
  key: string;
  label: string;
  description: string;
  /** Command called with { responseId } by the form's scenario; empty for «general» (history only). */
  process: string | null;
  /** Answer keys the processor needs; publishing is refused without them. */
  requires: string[];
  /** A yes/no field of the scope record that must be on for the form to take answers (sales open, CFP open). */
  scopeFlag?: { field: string; closedText: string };
  /** Fields that belong to a feature of the app (field key → `app:feature`): hidden and not taken while it is off (decision 025). */
  fieldFeatures?: Record<string, string>;
  steps: FormStep[];
}

export type FormAnswers = Record<string, unknown>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function visible(f: FormField, answers: FormAnswers): boolean {
  if (!f.showIf) return true;
  const v = answers[f.showIf.field];
  if (Array.isArray(f.showIf.equals)) return f.showIf.equals.includes(String(v ?? ''));
  return f.showIf.equals === true ? v === true || v === 'on' || v === '1' : String(v ?? '') === String(f.showIf.equals);
}

/** Checks answers against the form; the server runs it on submit, the client only for speed (DESIGN «Формы»). */
export function checkAnswers(steps: FormStep[], answers: FormAnswers): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const st of steps) for (const f of st.fields) {
    if (f.kind === 'heading' || !visible(f, answers)) continue;
    const v = answers[f.key];
    const empty = v == null || v === '' || (Array.isArray(v) && !v.length) || (f.kind === 'record_quantities' && !Object.values((v as Record<string, number>) ?? {}).some((n) => Number(n) > 0));
    if (f.kind === 'consent') { if (f.required && !(v === true || v === 'on' || v === '1')) errors[f.key] = 'Нужно согласие'; continue; }
    if (f.kind === 'file') {
      if (v != null && v !== '' && !isFileRef(v)) errors[f.key] = 'Файл не загрузился, выберите его ещё раз';
      else if (f.required && !isFileRef(v)) errors[f.key] = 'Прикрепите файл';
      continue;
    }
    if (empty) { if (f.required) errors[f.key] = 'Заполните поле'; continue; }
    if (f.kind === 'email' && !EMAIL.test(String(v))) errors[f.key] = 'Проверьте email';
    if (f.kind === 'phone' && String(v).replace(/\D/g, '').length < 10) errors[f.key] = 'Проверьте телефон';
    if (f.kind === 'number') { const n = Number(v); if (Number.isNaN(n)) errors[f.key] = 'Нужно число'; else if (f.min !== undefined && n < f.min) errors[f.key] = `Не меньше ${f.min}`; else if (f.max !== undefined && n > f.max) errors[f.key] = `Не больше ${f.max}`; }
    if (f.kind === 'url' && !/^https?:\/\//.test(String(v))) errors[f.key] = 'Ссылка начинается с http:// или https://';
    if (f.pattern && typeof v === 'string' && !new RegExp(`^(?:${f.pattern})$`).test(v.trim())) errors[f.key] = f.patternText ?? 'Проверьте формат';
    if (f.kind === 'select' && f.options?.length && !f.options.includes(String(v))) errors[f.key] = 'Выберите из списка';
    if (f.kind === 'people') {
      const list = (Array.isArray(v) ? v : []) as { name?: string; email?: string }[];
      if (f.max && list.length > f.max) errors[f.key] = `Не больше ${f.max}`;
      const bad = list.find((p) => (p.email && !EMAIL.test(p.email)) || (p.email && !p.name));
      if (bad) errors[f.key] = 'У каждого человека — имя и правильный email';
      const emails = list.map((p) => p.email?.toLowerCase()).filter(Boolean);
      if (new Set(emails).size !== emails.length) errors[f.key] = 'Один email указан дважды';
    }
  }
  return errors;
}

export const allFields = (steps: FormStep[]) => steps.flatMap((s) => s.fields);
export type { Id };
